import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUsers, createUser, toggleUserActive, resendInvitation, syncUsersFromHubhus } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Switch } from '../components/ui/switch';
import { 
  Plus, 
  Search, 
  MoreVertical, 
  Mail, 
  UserCog, 
  CheckCircle2, 
  XCircle,
  Loader2,
  AlertCircle,
  RefreshCw
} from 'lucide-react';

const AdminUsersPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [discFilter, setDiscFilter] = useState('all');
  
  // Create user dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'consultant',
    send_invitation: true
  });
  
  // Sync from Hubhus
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  useEffect(() => {
    loadUsers();
  }, [searchQuery, roleFilter, discFilter]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchQuery) params.search = searchQuery;
      if (roleFilter !== 'all') params.role = roleFilter;
      if (discFilter !== 'all') params.disc_status = discFilter;
      
      const response = await getUsers(params);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreateError('');
    setCreating(true);
    
    try {
      await createUser(newUser);
      setDialogOpen(false);
      setNewUser({
        name: '',
        email: '',
        phone: '',
        role: 'consultant',
        send_invitation: true
      });
      loadUsers();
    } catch (error) {
      setCreateError(error.response?.data?.detail || 'Kunne ikke oprette bruger');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (userId) => {
    try {
      await toggleUserActive(userId);
      loadUsers();
    } catch (error) {
      console.error('Failed to toggle user:', error);
    }
  };

  const handleResendInvite = async (userId) => {
    try {
      await resendInvitation(userId);
      // Show success toast here
    } catch (error) {
      console.error('Failed to resend invitation:', error);
    }
  };

  const handleSyncFromHubhus = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const response = await syncUsersFromHubhus();
      setSyncResult(response.data);
      if (response.data.created > 0) {
        loadUsers(); // Reload user list
      }
    } catch (error) {
      console.error('Failed to sync from Hubhus:', error);
      setSyncResult({ error: error.response?.data?.detail || 'Synkronisering fejlede' });
    } finally {
      setSyncing(false);
    }
  };

  const getDiscDisplay = (user) => {
    if (!user.disc_profile_json) return null;
    const { primary, secondary } = user.disc_profile_json;
    if (primary && secondary) return `${primary}/${secondary}`;
    if (primary) return primary;
    return null;
  };

  return (
    <div className="max-w-6xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-outfit">Medarbejdere</h1>
          <p className="text-slate-500 mt-1">Administrer konsulenter og admins</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            onClick={handleSyncFromHubhus}
            disabled={syncing}
            className="border-slate-200"
            data-testid="sync-hubhus-btn"
          >
            {syncing ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Synkroniser fra Hubhus
          </Button>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800" data-testid="create-user-btn">
                <Plus className="h-4 w-4 mr-2" />
                Opret medarbejder
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-outfit">Opret ny medarbejder</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
              {createError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-100 rounded-lg text-red-600 text-sm">
                  <AlertCircle className="h-4 w-4" />
                  <span>{createError}</span>
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="name">Navn *</Label>
                <Input
                  id="name"
                  value={newUser.name}
                  onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                  required
                  data-testid="new-user-name"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  required
                  data-testid="new-user-email"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="phone">Telefon</Label>
                <Input
                  id="phone"
                  value={newUser.phone}
                  onChange={(e) => setNewUser({...newUser, phone: e.target.value})}
                  data-testid="new-user-phone"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="role">Rolle *</Label>
                <Select 
                  value={newUser.role} 
                  onValueChange={(value) => setNewUser({...newUser, role: value})}
                >
                  <SelectTrigger data-testid="new-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="consultant">Konsulent</SelectItem>
                    <SelectItem value="installer">Montør</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center justify-between">
                <Label htmlFor="send_invitation">Send invitation email</Label>
                <Switch
                  id="send_invitation"
                  checked={newUser.send_invitation}
                  onCheckedChange={(checked) => setNewUser({...newUser, send_invitation: checked})}
                  data-testid="new-user-send-invite"
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-slate-900 hover:bg-slate-800"
                disabled={creating}
                data-testid="submit-create-user"
              >
                {creating ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                Opret medarbejder
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Sync Result Feedback */}
      {syncResult && (
        <Card className={`border mb-6 ${syncResult.error ? 'border-red-200 bg-red-50' : 'border-emerald-200 bg-emerald-50'}`}>
          <CardContent className="p-4">
            {syncResult.error ? (
              <div className="flex items-center gap-2 text-red-700">
                <AlertCircle className="h-5 w-5" />
                <span>{syncResult.error}</span>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-emerald-700 font-medium">
                  <CheckCircle2 className="h-5 w-5" />
                  <span>{syncResult.message}</span>
                </div>
                <div className="text-sm text-emerald-600">
                  <span className="font-semibold">{syncResult.created}</span> nye brugere oprettet · 
                  <span className="font-semibold ml-1">{syncResult.skipped}</span> allerede eksisterende
                </div>
                {syncResult.created_users && syncResult.created_users.length > 0 && (
                  <div className="mt-2 text-sm text-emerald-600">
                    <span className="font-medium">Nye brugere:</span>{' '}
                    {syncResult.created_users.map(u => u.name).join(', ')}
                    {syncResult.created > 10 && ` ... og ${syncResult.created - 10} flere`}
                  </div>
                )}
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="mt-2 text-emerald-700 hover:text-emerald-800 hover:bg-emerald-100"
                  onClick={() => setSyncResult(null)}
                >
                  Luk
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card className="border-slate-100 mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Søg efter navn eller email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="search-users"
              />
            </div>
            
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-[160px]" data-testid="filter-role">
                <SelectValue placeholder="Rolle" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle roller</SelectItem>
                <SelectItem value="consultant">Konsulent</SelectItem>
                <SelectItem value="installer">Montør</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={discFilter} onValueChange={setDiscFilter}>
              <SelectTrigger className="w-[160px]" data-testid="filter-disc">
                <SelectValue placeholder="DISC status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle DISC</SelectItem>
                <SelectItem value="ok">DISC OK</SelectItem>
                <SelectItem value="missing">DISC mangler</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card className="border-slate-100">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : users.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-slate-500">Ingen medarbejdere fundet</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Navn</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Rolle</TableHead>
                  <TableHead>DISC</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow 
                    key={user.id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => navigate(`/admin/users/${user.id}`)}
                    data-testid={`user-row-${user.id}`}
                  >
                    <TableCell className="font-medium">{user.name}</TableCell>
                    <TableCell className="text-slate-500">{user.email}</TableCell>
                    <TableCell>
                      <div className="flex gap-1.5 items-center">
                        <Badge variant="outline" className={
                          user.role === 'admin' 
                            ? 'bg-purple-50 text-purple-700 border-purple-100' 
                            : 'bg-slate-100 text-slate-700'
                        }>
                          {user.role === 'admin' ? 'Admin' : 'Konsulent'}
                        </Badge>
                        {user.is_installer && (
                          <Badge className="bg-teal-50 text-teal-700 border-teal-100">
                            Montør
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {user.disc_status === 'ok' ? (
                        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-100">
                          {getDiscDisplay(user)}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-amber-600 border-amber-200">
                          Mangler
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {user.is_active ? (
                        <span className="flex items-center gap-1 text-emerald-600">
                          <CheckCircle2 className="h-4 w-4" />
                          Aktiv
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-slate-400">
                          <XCircle className="h-4 w-4" />
                          Inaktiv
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/admin/users/${user.id}`);
                          }}>
                            <UserCog className="h-4 w-4 mr-2" />
                            Rediger
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => {
                            e.stopPropagation();
                            handleResendInvite(user.id);
                          }}>
                            <Mail className="h-4 w-4 mr-2" />
                            Send invitation
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => {
                            e.stopPropagation();
                            handleToggleActive(user.id);
                          }}>
                            {user.is_active ? (
                              <>
                                <XCircle className="h-4 w-4 mr-2" />
                                Deaktiver
                              </>
                            ) : (
                              <>
                                <CheckCircle2 className="h-4 w-4 mr-2" />
                                Aktiver
                              </>
                            )}
                          </DropdownMenuItem>
                          {user.role !== 'admin' && (
                            <DropdownMenuItem onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                await axios.put(`${API}/admin/users/${user.id}`, 
                                  { is_installer: !user.is_installer }, 
                                  { headers }
                                );
                                loadUsers();
                              } catch (err) {
                                console.error(err);
                              }
                            }}>
                              {user.is_installer ? (
                                <>
                                  <UserCog className="h-4 w-4 mr-2" />
                                  Fjern montør-rolle
                                </>
                              ) : (
                                <>
                                  <UserCog className="h-4 w-4 mr-2" />
                                  Markér som montør
                                </>
                              )}
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminUsersPage;
